import type { LocationQueryRaw } from 'vue-router'
import {
  LocaleClass,
  LocaleToApi,
  ApiToLocale,
  LocalStorage
} from '@library/enums'
import { Locale, type ApiLocale } from '@library/enums'
import { AppI18n } from '@library/locales'
import { useStorage } from './use-storage'

type LangKey<T extends string> =
  | `traditional${T}`
  | `english${T}`
  | `simplified${T}`

export function useLocale() {
  const i18n = AppI18n.i18n

  const { setStorageData } = useStorage()

  const apiLang = computed((): ApiLocale => {
    return LocaleToApi[i18n.global.locale.value]
  })

  function setLocaleByApiLang(val: ApiLocale, query?: LocationQueryRaw) {
    const lang = ApiToLocale[val]

    if (lang) {
      setLocaleValue(lang, query)
    }
  }

  /** 設定 i18n local value */
  function setLocaleValue(value: Locale, query?: LocationQueryRaw) {
    if (!i18n) {
      return
    }

    i18n.global.locale.value = value

    // route.query.preventStorageLocale=true 時，不寫入localStorage
    if (!query?.preventStorageLocale) {
      setStorageData(LocalStorage.LOCALE, value)
    }
  }

  /** 根據語系設定 html lang 及 body class */
  function setLangAndBodyClass(locale: Locale) {
    const classList = document.querySelector('body')?.classList
    if (classList) {
      classList.forEach((className) => {
        if (className.startsWith('font-')) {
          classList.remove(className)
        }
      })
    }
    classList?.add('font-' + LocaleClass[locale])

    document.documentElement.lang = i18n.global.locale.value
  }

  function getDataByLang<T extends { lang: ApiLocale }>(
    list: T[] | null
  ): T | undefined {
    return list?.find(({ lang }) => lang === apiLang.value)
  }

  function getLangText<
    K extends string,
    R extends string | string[] = string,
    U extends Record<LangKey<K>, R> = Record<LangKey<K>, R>
  >(data: U, key: K, lang?: Locale): R {
    const defaultLang = lang ?? i18n.global.locale.value

    switch (defaultLang) {
      case Locale.ZH_CN:
        return data[`simplified${key}`]
      case Locale.ZH_TW:
        return data[`traditional${key}`]
      default:
        return data[`english${key}`]
    }
  }

  return {
    i18n,
    apiLang,
    getDataByLang,
    getLangText,
    setLocaleValue,
    setLocaleByApiLang,
    setLangAndBodyClass
  }
}
